import axios from "../config/axios-chatbot";
import qs from "qs";
import {currentRouteFlowUid, getFlowProtectionToken} from "../utils/flowProtectionStorage";

// Attaches the Flow Protection unlock token (localStorage `${flowUid}_token`) if one exists.
// flowUid defaults to the project in the current route.
const protectionHeaders = (flowUid) => {
    const protectionToken = getFlowProtectionToken(flowUid || currentRouteFlowUid());
    return protectionToken ? {"X-Protection-Token": protectionToken} : {};
};

export const ConversationRepository = {
    getStatusStatistic: ({flowUid, type, startDate, endDate, signal}) =>
        axios.get(`/api/conversation/statistics/status`, {
            params: {
                flow: flowUid,
                type,
                start: startDate,
                end: endDate
            },
            signal,
            headers: protectionHeaders(flowUid)
        }),
    // signal: aborts the request (not sent as a query parameter)
    getConversations: ({signal, ...params}) =>
        axios.get(`/api/conversation`, {
            params: {...(params || {})},
            signal,
            paramsSerializer: (params) =>
                qs.stringify(params, {arrayFormat: "repeat"}), // <-- key fix
            headers: protectionHeaders(params?.flow)
        }),
    // Every conversation matching the getConversations filters (not paged) as a CSV file (Blob)
    exportConversations: ({...params}) =>
        axios.get(`/api/conversation/export`, {
            params: {...(params || {})},
            paramsSerializer: (params) => qs.stringify(params, {arrayFormat: "repeat"}),
            responseType: "blob",
            headers: protectionHeaders(params?.flow)
        }),
    getConversation: ({id, flowUid}) => axios.get(`/api/conversation/${id}`, {
        headers: protectionHeaders(flowUid)
    }),
    getPrevAndNext: ({id, ...params}) => axios.get(`/api/conversation/${id}/navigation`, {
        params: {...(params || {})},
        headers: protectionHeaders(params?.flow)
    }),
    handoverToAgent: ({id, username, vdn, flowUid, ...params}) => axios.post(`/api/conversation/${id}/handover`, {
        id: id,
        username: username,
        vdn: vdn
    }, {headers: protectionHeaders(flowUid)}),
    toggleResolved: ({id, flowUid}) => axios.post(`/api/conversation/${id}/toggle-resolve`, undefined, {
        headers: protectionHeaders(flowUid)
    }),
    monitorConversation: ({id, username, flowUid, ...params}) => axios.post(`/api/conversation/${id}/monitoring`, {
        id: id,
        username: username
    }, {headers: protectionHeaders(flowUid)}),
    initiateConversation: ({
                               body,
                               params,
                               flowUid
                           }) => axios.post(`/api/conversation/call-over`, body, {
        params: {...(params || {})},
        headers: protectionHeaders(flowUid)
    }),
    getMessages: (id, signal, flowUid) => axios.get(`/api/conversation/${id}/messages`, {
        signal,
        headers: protectionHeaders(flowUid)
    }),
    getHistory: ({id, caller, type, page, size, flowUid}) => axios.get(`/api/conversation/history`, {
        params: {
            id,
            caller,
            type,
            page,
            size
        },
        headers: protectionHeaders(flowUid)
    }),
    createConversation: (data) => axios.post("/api/conversation", data, {
        headers: protectionHeaders(data?.flowUid)
    }),
    // Audio by id (the stored paths are server-side only). Blob responses: play them from a blob URL, an <audio src>
    // cannot send the Authorization header (see utils/audioLoader). download: true = Content-Disposition attachment.
    getConversationAudio: ({id, flowUid, download, signal}) => axios.get(`/api/conversation/${id}/audio`, {
        params: download ? {download: true} : {},
        responseType: "blob",
        signal,
        headers: protectionHeaders(flowUid)
    }),
    getMessageAudio: ({conversationId, messageId, flowUid, download, signal}) =>
        axios.get(`/api/conversation/${conversationId}/messages/${messageId}/audio`, {
            params: download ? {download: true} : {},
            responseType: "blob",
            signal,
            headers: protectionHeaders(flowUid)
        }),
    sendMessage: ({id, message, flowUid}) => axios.post(`/api/conversation/${id}/send`, {message, conversation: id}, {
        headers: protectionHeaders(flowUid)
    }),
    getNodeUsage: (ids) => {
        const query = ids.map(id => `ids=${encodeURIComponent(id)}`).join('&');
        return axios.get(`/api/conversation/statistics/node-usage?${query}`, {
            headers: protectionHeaders()
        })
    },
    likeConversation: ({id, likeType, username, flowUid}) => axios.post(`/api/conversation/${id}/interactions/likes`, {
        likeType,
        username
    }, {headers: protectionHeaders(flowUid)}),
    removeLike: ({id, likeId, flowUid}) => axios.delete(`/api/conversation/${id}/interactions/likes/${likeId}`, {
        headers: protectionHeaders(flowUid)
    }),

    commentConversation: ({id, text, username, flowUid}) => axios.post(`/api/conversation/${id}/interactions/comments`, {
        text,
        username
    }, {headers: protectionHeaders(flowUid)}),
    removeComment: ({id, commentId, flowUid}) => axios.delete(`/api/conversation/${id}/interactions/comments/${commentId}`, {
        headers: protectionHeaders(flowUid)
    }),

}
